import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// ---- Session-level memo cache (stale-while-revalidate) -----------------
// Reduces perceived load time when users navigate between pages: read-only
// endpoints (locations, categories, specials, menus, brands…) get cached
// for `ttlMs` in-memory so subsequent paints return instantly. Two
// upgrades over a plain memo:
//   1. SWR — if the cache is stale we return the stale value AND fire a
//      background refresh, so the UI never blocks once the user has seen
//      a page during this session.
//   2. sessionStorage hydration — heavy endpoints (menu, brand, specials,
//      strain library) persist their last response to sessionStorage so a
//      hard reload still feels instant within the same tab.
const _memo = new Map(); // key → { expires, value }
const _inflight = new Map(); // key → Promise

const _SS = typeof window !== "undefined" ? window.sessionStorage : null;
function _ssGet(key) {
    if (!_SS) return null;
    try {
        const raw = _SS.getItem(`zen:${key}`);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        return null;
    }
}
function _ssSet(key, value, expires) {
    if (!_SS) return;
    try {
        _SS.setItem(`zen:${key}`, JSON.stringify({ value, expires }));
    } catch (e) {
        /* quota exceeded — silently drop */
        if (typeof console !== "undefined")
            console.debug("[zen-api] sessionStorage write failed:", e?.name);
    }
}

function cached(key, ttlMs, fetcher, { persist = false, swr = true } = {}) {
    const now = Date.now();
    // 1. In-memory fresh hit.
    let hit = _memo.get(key);
    // 2. Rehydrate from sessionStorage if memory is cold but persisted entry exists.
    if (!hit && persist) {
        const ss = _ssGet(key);
        if (ss && ss.value !== undefined) {
            hit = { expires: ss.expires || 0, value: ss.value };
            _memo.set(key, hit);
        }
    }
    const isFresh = hit && hit.expires > now;
    if (isFresh) return Promise.resolve(hit.value);

    // 3. SWR — if we have a STALE value, kick off a background refresh and
    //    return the stale data immediately. The next render after the
    //    refresh resolves will see the updated cache.
    if (swr && hit && !_inflight.get(key)) {
        const refresh = fetcher()
            .then((v) => {
                _memo.set(key, { expires: Date.now() + ttlMs, value: v });
                if (persist) _ssSet(key, v, Date.now() + ttlMs);
                _inflight.delete(key);
                return v;
            })
            .catch((e) => {
                _inflight.delete(key);
                throw e;
            });
        _inflight.set(key, refresh);
        return Promise.resolve(hit.value);
    }

    // 4. No data anywhere — fetch, dedupe in-flight, persist if asked.
    const pending = _inflight.get(key);
    if (pending) return pending;
    const p = fetcher()
        .then((v) => {
            _memo.set(key, { expires: Date.now() + ttlMs, value: v });
            if (persist) _ssSet(key, v, Date.now() + ttlMs);
            _inflight.delete(key);
            return v;
        })
        .catch((e) => {
            _inflight.delete(key);
            throw e;
        });
    _inflight.set(key, p);
    return p;
}

export const getBrand = () =>
    cached("brand", 10 * 60_000, () => api.get("/brand").then((r) => r.data));
export const getLocations = () =>
    cached("locations", 10 * 60_000, () =>
        api.get("/locations").then((r) => r.data)
    );
export const getLocation = (slug) =>
    cached(`loc:${slug}`, 10 * 60_000, () =>
        api.get(`/locations/${slug}`).then((r) => r.data)
    );
export const getCities = () =>
    cached("cities", 30 * 60_000, () =>
        api.get("/cities").then((r) => r.data)
    );
export const getCity = (slug) =>
    cached(`city:${slug}`, 30 * 60_000, () =>
        api.get(`/cities/${slug}`).then((r) => r.data)
    );
export const getNearest = ({ zip, city }) =>
    api.get("/nearest", { params: { zip, city } }).then((r) => r.data);
export const getCategories = () =>
    cached("categories", 30 * 60_000, () =>
        api.get("/categories").then((r) => r.data)
    );
export const getBlog = () =>
    cached("blog", 5 * 60_000, () => api.get("/blog").then((r) => r.data));
export const getPost = (slug) =>
    cached(`post:${slug}`, 5 * 60_000, () =>
        api.get(`/blog/${slug}`).then((r) => r.data)
    );
export const getSitemapData = () =>
    cached("sitemap-data", 30 * 60_000, () =>
        api.get("/sitemap-data").then((r) => r.data)
    );

// ---- Dutchie Plus (live menu) ----
// Menu has per-query params; we still memoize by full param signature for the
// duration of the page session so paginating back/forward feels instant.
export const getMenu = (storeSlug, params = {}) => {
    const sig =
        `menu:${storeSlug}:` +
        Object.keys(params)
            .sort()
            .map((k) => `${k}=${params[k]}`)
            .join("&");
    return cached(
        sig,
        60_000,
        () => api.get(`/menu/${storeSlug}`, { params }).then((r) => r.data),
        { persist: true }
    );
};

export const getMenuProduct = (storeSlug, productSlug) =>
    cached(
        `prod:${storeSlug}:${productSlug}`,
        5 * 60_000,
        () =>
            api
                .get(`/menu/${storeSlug}/product/${productSlug}`)
                .then((r) => r.data),
        { persist: true }
    );

export const getSpecials = (storeSlug) =>
    cached(
        `specials:${storeSlug}`,
        60_000,
        () => api.get(`/specials/${storeSlug}`).then((r) => r.data),
        { persist: true }
    );

export const getSpecialDetail = (storeSlug, specialId) =>
    cached(`special:${storeSlug}:${specialId}`, 60_000, () =>
        api.get(`/specials/${storeSlug}/${specialId}`).then((r) => r.data)
    );

export const getBrandDetail = (storeSlug, brandSlug) =>
    cached(
        `brand:${storeSlug}:${brandSlug}`,
        5 * 60_000,
        () => api.get(`/menu/${storeSlug}/brand/${brandSlug}`).then((r) => r.data),
        { persist: true }
    );

export const getBrandsDirectory = (storeSlug) =>
    cached(
        `brands:${storeSlug}`,
        5 * 60_000,
        () => api.get(`/brands/${storeSlug}`).then((r) => r.data),
        { persist: true }
    );

export const getStrainLibrary = (storeSlug) =>
    cached(
        `strains:${storeSlug}`,
        10 * 60_000,
        () => api.get(`/strain-library/${storeSlug}`).then((r) => r.data),
        { persist: true }
    );

// ---- Prefetch helpers (hover-to-warm-the-cache) ------------------------
// Wire these to onMouseEnter handlers on nav Links + product cards. The
// fetch fires once per session; if data is already cached this is a no-op.
// Hover-prefetch — best-effort; silent on failure because the user never
// initiated this fetch directly (it warms the cache for the next click).
export const prefetchBrand = (storeSlug, brandSlug) => {
    getBrandDetail(storeSlug, brandSlug).catch(() => {});
};
export const prefetchProduct = (storeSlug, productSlug) => {
    getMenuProduct(storeSlug, productSlug).catch(() => {});
};
export const prefetchMenu = (storeSlug) => {
    getMenu(storeSlug, { sort_key: "POPULAR", limit: 24 }).catch(() => {});
    getSpecials(storeSlug).catch(() => {});
};
export const prefetchStrainLibrary = (storeSlug) => {
    getStrainLibrary(storeSlug).catch(() => {});
};

// ---- Native cart (Dutchie-backed) ----
// NEVER memoize cart mutations — they're stateful.
export const cartCreate = (storeSlug, orderType = "PICKUP") =>
    api
        .post(`/cart/${storeSlug}/create`, { order_type: orderType })
        .then((r) => r.data);

export const cartGet = (storeSlug, checkoutId) =>
    api.get(`/cart/${storeSlug}/${checkoutId}`).then((r) => r.data);

export const cartAddItem = (storeSlug, checkoutId, { productId, quantity, option }) =>
    api
        .post(`/cart/${storeSlug}/${checkoutId}/items`, {
            product_id: productId,
            quantity,
            option,
        })
        .then((r) => r.data);

export const cartUpdateQty = (storeSlug, checkoutId, itemId, quantity) =>
    api
        .patch(`/cart/${storeSlug}/${checkoutId}/items/${itemId}`, { quantity })
        .then((r) => r.data);

export const cartRemoveItem = (storeSlug, checkoutId, itemId) =>
    api.delete(`/cart/${storeSlug}/${checkoutId}/items/${itemId}`).then((r) => r.data);

export const cartUpdateCheckout = (storeSlug, checkoutId, payload) =>
    api.patch(`/cart/${storeSlug}/${checkoutId}`, payload).then((r) => r.data);

// Invalidate cached read-only data — useful after a backend mutation if a
// component wants the freshest read on next render.
export const invalidateCache = (prefix) => {
    if (!prefix) {
        _memo.clear();
        return;
    }
    for (const k of Array.from(_memo.keys())) {
        if (k.startsWith(prefix)) _memo.delete(k);
    }
};
